import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Print } from "@mui/icons-material";
import axios from "../axios";
import { useRef, useState } from "react";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import logo from "./images/logo.png";

const arrearsTableHead = (
  <TableHead>
    <TableRow>
      <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
      <TableCell sx={{ fontWeight: "bold" }}>Period</TableCell>
      <TableCell sx={{ fontWeight: "bold" }}>Class</TableCell>
      <TableCell align="right" sx={{ fontWeight: "bold" }}>
        Due Amount
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: "bold" }}>
        Discount
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: "bold" }}>
        Paid
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: "bold" }}>
        Outstanding
      </TableCell>
      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
    </TableRow>
  </TableHead>
);

const getStatusColor = (status) => {
  if (status === "PARTIALLY_SETTLED") return "warning";
  return "error";
};

const ArrearsReport = () => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: "@page { size: A5 landscape; }",
  });

  const { data: students } = useQuery("students", () =>
    axios("/students").then((res) => res.data)
  );

  const { data: arrearsData, isLoading } = useQuery(
    ["arrears", selectedStudent?.id],
    () =>
      axios("/reports/arrears", {
        params: { studentId: selectedStudent?.id },
      }).then((res) => res.data),
    {
      enabled: !!selectedStudent?.id,
    }
  );

  const hasArrears =
    arrearsData &&
    arrearsData.siblings?.some((s) => s.arrears.length > 0);

  return (
    <Box>
      <Typography variant="h5" component="h2">
        Arrears Report
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Autocomplete
          sx={{ width: 500 }}
          options={students || []}
          onChange={(_, student) => setSelectedStudent(student)}
          getOptionLabel={(student) => `${student.id} | ${student.fullName}`}
          renderInput={(params) => (
            <TextField {...params} label="Select Student" />
          )}
        />

        {hasArrears && (
          <Button
            onClick={handlePrint}
            variant="outlined"
            color="primary"
            startIcon={<Print />}
          >
            Print
          </Button>
        )}
      </Box>

      {!selectedStudent && (
        <Box marginTop={4}>
          <Typography color="textSecondary">
            Please select a student to view their arrears.
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" marginTop={4}>
          <CircularProgress />
        </Box>
      )}

      {arrearsData && selectedStudent && (
        <>
          {/* Parent info + grand total */}
          <Box
            sx={{
              mt: 3,
              mb: 1,
              display: "flex",
              gap: 4,
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" color="textSecondary">
                Parent
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {arrearsData.parent.fullName || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Grand Total Outstanding
              </Typography>
              <Typography variant="h6" fontWeight={600} color="error">
                Rs. {arrearsData.grandTotalOutstanding.toLocaleString()}
              </Typography>
            </Box>
          </Box>

          {!hasArrears && (
            <Box marginTop={2}>
              <Typography color="textSecondary">
                No outstanding arrears found up to the current month.
              </Typography>
            </Box>
          )}

          {/* Sibling sections */}
          {arrearsData.siblings.map((sibling) => (
            <Box key={sibling.student.id} sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={600} component="h3">
                {sibling.student.fullName} | {sibling.student.className || "-"}
              </Typography>

              {sibling.arrears.length === 0 ? (
                <Typography color="textSecondary" sx={{ mt: 1 }}>
                  No outstanding arrears.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    {arrearsTableHead}
                    <TableBody>
                      {sibling.arrears.map((row) => (
                        <TableRow key={row.feeId}>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>{row.period}</TableCell>
                          <TableCell>{row.className}</TableCell>
                          <TableCell align="right">
                            {row.dueAmount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.discount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.paidAmount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.outstanding.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                row.status === "PARTIALLY_SETTLED"
                                  ? "Partial"
                                  : "Pending"
                              }
                              color={getStatusColor(row.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Subtotal row */}
                      <TableRow
                        sx={{
                          backgroundColor: "#f5f5f5",
                          "& td": { fontWeight: "bold" },
                        }}
                      >
                        <TableCell colSpan={3}>Subtotal</TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalDue.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalDiscount.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalPaid.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalOutstanding.toLocaleString()}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ))}

          {/* Grand total across all siblings */}
          {hasArrears && arrearsData.siblings.length > 1 && (
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table size="small">
                <TableBody>
                  <TableRow
                    sx={{
                      backgroundColor: "#e0e0e0",
                      "& td": { fontWeight: "bold", fontSize: "0.95rem" },
                    }}
                  >
                    <TableCell colSpan={3}>Grand Total</TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalDue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalDiscount.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalPaid.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalOutstanding.toLocaleString()}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Printable version */}
      {hasArrears && (
        <Box
          ref={printRef}
          className="print"
          sx={{ display: "none", "@media print": { display: "block" } }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <img src={logo} alt="logo" width={110} />
            <Box>
              <Typography
                variant="h5"
                fontWeight={600}
                component="h1"
                align="center"
              >
                Mount Royal International School
              </Typography>
              <Typography variant="body1" component="p" align="center">
                No 67, Kawdana Road, Dehiwala.
              </Typography>
              <Typography variant="body1" component="p" align="center">
                0112712149
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} component="h2">
                Arrears Report
              </Typography>
              <Typography variant="body2" component="p" align="right">
                {new Date().toLocaleDateString("en-GB")}
              </Typography>
            </Box>
          </Box>

          {/* Parent info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Parent:</strong> {arrearsData.parent.fullName || "-"}
            </Typography>
          </Box>

          {/* Each sibling's table */}
          {arrearsData.siblings.map((sibling) => {
            if (sibling.arrears.length === 0) return null;
            return (
              <Box key={sibling.student.id} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontSize: "1rem", mb: 0.5 }}
                >
                  {sibling.student.fullName} |{" "}
                  {sibling.student.className || "-"}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Period
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Class</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Due Amount
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Discount
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Paid
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Outstanding
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sibling.arrears.map((row) => (
                        <TableRow key={row.feeId}>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>{row.period}</TableCell>
                          <TableCell>{row.className}</TableCell>
                          <TableCell align="right">
                            {row.dueAmount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.discount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.paidAmount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.outstanding.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {row.status === "PARTIALLY_SETTLED"
                              ? "Partial"
                              : "Pending"}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ "& td": { fontWeight: "bold" } }}>
                        <TableCell colSpan={3}>Subtotal</TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalDue.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalDiscount.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalPaid.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {sibling.totalOutstanding.toLocaleString()}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

          {/* Grand total in print */}
          {arrearsData.siblings.length > 1 && (
            <Box sx={{ mt: 1 }}>
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ "& td": { fontWeight: "bold" } }}>
                    <TableCell colSpan={3}>Grand Total</TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalDue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalDiscount.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalPaid.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {arrearsData.grandTotalOutstanding.toLocaleString()}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ArrearsReport;
